import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, RevealGroup, HEADING } from "@/components/site/Reveal";
import { getTeam, getTeamMember } from "@/lib/cms";
import { absoluteUrl } from "@/lib/site";
import { ldScript, personSchema, breadcrumbSchema } from "@/lib/schema";

export const Route = createFileRoute("/team/$slug")({
  loader: ({ params }) => {
    const member = getTeamMember(params.slug);
    if (!member) throw notFound();
    return member;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Team Member Not Found · SunSmart Engineering" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const { name, "job-title": jobTitle, summary, slug, "team-member-photo": photo } = loaderData.fieldData;
    const title = `${name} · ${jobTitle} · SunSmart Engineering`;
    const url = absoluteUrl(`/team/${slug}`);
    const image = photo?.url ? absoluteUrl(photo.url) : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: summary },
        { property: "og:title", content: title },
        { property: "og:description", content: summary },
        { property: "og:url", content: url },
        { property: "og:type", content: "profile" },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ldScript(
          personSchema({
            name,
            jobTitle,
            url,
            image,
            description: summary,
          }),
        ),
        ldScript(
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Team", url: "/team" },
            { name, url },
          ]),
        ),
      ],
    };
  },
  notFoundComponent: TeamMemberNotFound,
  component: TeamMemberPage,
});

function TeamMemberNotFound() {
  return (
    <PageLayout kicker="Team" title="Not found">
      <PageSection>
        <p className="text-lg text-muted-foreground">
          We couldn't find that team member. They may have moved on, or the link is out of date.
        </p>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
        >
          Back to home <span>→</span>
        </Link>
      </PageSection>
    </PageLayout>
  );
}

function TeamMemberPage() {
  const member = Route.useLoaderData();
  const { name, "job-title": jobTitle, "team-member-photo": photo, summary, bio } = member.fieldData;

  const team = getTeam();
  const idx = team.findIndex((m) => m.fieldData.slug === member.fieldData.slug);
  const others = team.filter((m) => m.fieldData.slug !== member.fieldData.slug).slice(0, 3);
  const prev = idx > 0 ? team[idx - 1] : undefined;
  const next = idx >= 0 && idx < team.length - 1 ? team[idx + 1] : undefined;

  return (
    <PageLayout kicker={jobTitle} title={name}>
      {/* Photo + summary + contact CTA */}
      <PageSection label="01" title="Meet the team">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-sm border border-border bg-surface">
              <div className="aspect-[4/5]">
                <img
                  src={photo.url}
                  alt={name}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <RevealGroup stagger={70} maxStagger={220}>
              <p className={`${HEADING.lead} mt-0`}>{summary}</p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
              >
                Get in touch with our team <span>→</span>
              </Link>
              <div
                className="mt-12 max-w-[62ch] [&_p]:mb-4 [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p:last-child]:mb-0 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-muted-foreground [&_ul]:space-y-1 [&_li]:leading-relaxed [&_strong]:text-foreground [&_strong]:font-semibold [&_em]:italic"
                dangerouslySetInnerHTML={{ __html: bio }}
              />
            </RevealGroup>
          </div>
        </div>
      </PageSection>

      {/* More of the team */}
      <PageSection label="02" title="More of the team">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((m, i) => (
            <Reveal key={m.id} delay={i * 100}>
              <Link
                to="/team/$slug"
                params={{ slug: m.fieldData.slug }}
                className="group block overflow-hidden rounded-sm border border-border bg-surface"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={m.fieldData["team-member-photo"].url}
                    alt={m.fieldData.name}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:opacity-100 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-6">
                  <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-sun">
                    {m.fieldData["job-title"]}
                  </div>
                  <h3 className="mt-3 font-display text-xl font-black uppercase leading-tight">
                    {m.fieldData.name}
                  </h3>
                  <div className="mt-4 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun">
                    View profile <span className="transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {(prev || next) && (
          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
            {prev ? (
              <Link
                to="/team/$slug"
                params={{ slug: prev.fieldData.slug }}
                className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun hover:text-foreground transition-colors"
              >
                <span className="transition-transform group-hover:-translate-x-1">←</span> {prev.fieldData.name}
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link
                to="/team/$slug"
                params={{ slug: next.fieldData.slug }}
                className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun hover:text-foreground transition-colors"
              >
                {next.fieldData.name} <span>→</span>
              </Link>
            )}
          </div>
        )}
      </PageSection>
    </PageLayout>
  );
}
